"use client";

import { cn } from "@/lib/utils";

import { VS_ACTION_BAR_GRADIENT } from "./constants";
import { VsGrainOverlay } from "./grain-overlay";

interface ActionBarProps {
  text: string;
  ctaLabel?: string;
  ctaHref?: string;
  ariaLabel?: string;
  className?: string;
}

/**
 * Top-of-page event action bar. Renders a gradient sampled from the hero
 * video's colour palette, then layers a translucent white tint on top so
 * the bar reads a step lighter than the hero — enough contrast to see the
 * seam, but not so much that the bar stops feeling part of the same colour
 * story.
 */
export function ActionBar({
  text,
  ctaLabel,
  ctaHref,
  ariaLabel = "Site announcement",
  className,
}: ActionBarProps) {
  return (
    <div
      role="region"
      aria-label={ariaLabel}
      className={cn(
        "fixed inset-x-0 top-0 z-[60] flex h-[72px] items-center justify-center overflow-hidden bg-[#06141b] px-6 md:h-9 md:px-[88px]",
        className
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ backgroundImage: VS_ACTION_BAR_GRADIENT }}
      />
      <VsGrainOverlay />
      {/* Subtle white wash above the gradient — lifts the bar a notch
          lighter than the hero so the seam reads, without losing the
          gradient continuity. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-white/10"
      />
      <p className="relative mx-auto w-full max-w-[1440px] text-center text-[14px] leading-[1.4] tracking-[-0.02em] text-white">
        {text}
        {ctaLabel && ctaHref && (
          <>
            {" "}
            <a
              href={ctaHref}
              className={cn(
                "underline underline-offset-2 transition-opacity hover:opacity-80 motion-reduce:transition-none",
                "rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1 focus-visible:ring-offset-transparent"
              )}
            >
              {ctaLabel}
            </a>
          </>
        )}
      </p>
    </div>
  );
}
